import type { PortalCalendarAppointmentItem } from "../types/portal.types.ts"

/** No customer-facing ICS export exists on the backend (the staff
 * `/calendars/events/{id}/ics/` route is gated on staff `calendar_*`
 * permissions the portal doesn't have) — this builds a minimal, correct
 * `.ics` file client-side from the fields the portal already has. A
 * one-way download, not a synchronized calendar subscription. */
const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n")

const toIcsUtc = (iso: string): string => {
  const date = new Date(iso)
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
}

export const buildAppointmentIcs = (
  item: PortalCalendarAppointmentItem
): string => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//EZBuild//Customer Portal//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${item.id}@ezbuild.app`,
    `DTSTAMP:${toIcsUtc(new Date().toISOString())}`,
    `DTSTART:${toIcsUtc(item.start_at)}`,
    `DTEND:${toIcsUtc(item.end_at)}`,
    `SUMMARY:${escapeIcsText(item.title)}`
  ]
  if (item.location) lines.push(`LOCATION:${escapeIcsText(item.location)}`)
  if (item.meeting_link) lines.push(`URL:${item.meeting_link}`)
  if (item.project_name) {
    lines.push(`DESCRIPTION:${escapeIcsText(`Project: ${item.project_name}`)}`)
  }
  lines.push("END:VEVENT", "END:VCALENDAR")
  return lines.join("\r\n")
}

export const downloadAppointmentIcs = (
  item: PortalCalendarAppointmentItem
): void => {
  const blob = new Blob([buildAppointmentIcs(item)], {
    type: "text/calendar;charset=utf-8"
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `${item.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "appointment"}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
